/**
 * ADR-0115 — the Model Advisor chat, in the inspector "Advisor" tab on ML nodes.
 * Node-scoped, read-only advice. Built on the SAME contract as the other Atlas
 * inspector chats (Reactive Viz / AI Provider): `useAiChatThread` owns the durable
 * lifecycle — load-on-mount + poll-while-a-user-turn-is-trailing — so submitting
 * a message, clicking away, and coming back keeps the pending state and the reply.
 * `inFlight = chatBusy || replyPending` (the hook's signal), NOT local run state.
 * The agent_runs SSE stream is only a fast-flush optimization on top of the poll.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { apiFetch } from "../lib/api.js";
import { useAiChatThread, type AiChatMessage } from "../hooks/useAiChatThread.js";
import { startNewAdvisorThread } from "../lib/advisorThread.js";
import { useAgentRunStream } from "../hooks/useAgentRunStream.js";
import { ChatMessageList, ChatComposer, ChatErrorBanner, ChatThinkingIndicator } from "./aiChat/index.js";
import { aiRouteErrorCode } from "../lib/aiRouteError.js";
import { useToast } from "./Toast.js";
import {
  summarizeChange,
  type ModelConfigChange,
  type ApplyModelConfigResult,
} from "../lib/modelConfigChange.js";

/** ADR-0116 §6.3 — a propose_model_config_change rendered with Apply. Works for any
 *  model node (train config or predict/evaluate binding). The seam validates +
 *  (for destructive train deltas) confirms; the change is PROPOSED until applied. */
function ProposedChangeCard({
  change,
  nodeId,
  apply,
  resolveModelName,
}: {
  change: ModelConfigChange;
  nodeId: string;
  apply: (nodeId: string, change: ModelConfigChange) => Promise<ApplyModelConfigResult>;
  resolveModelName?: (id: string) => string | null;
}): JSX.Element {
  const toast = useToast();
  const [applying, setApplying] = useState(false);
  const [applied, setApplied] = useState(false);

  const onApply = async () => {
    setApplying(true);
    try {
      const res = await apply(nodeId, change);
      if (res.ok) setApplied(true);
      else if (!res.cancelled) toast.push({ kind: "error", message: res.reason });
    } finally {
      setApplying(false);
    }
  };

  if (applied) {
    return (
      <div className="help" style={{ marginTop: 6 }}>
        ✓ Applied.
      </div>
    );
  }

  return (
    <div className="card" style={{ marginTop: 6, padding: 8, display: "grid", gap: 6 }}>
      <div className="label">Proposed change</div>
      <div className="help">{summarizeChange(change, resolveModelName)}</div>
      <div className="actions">
        <button type="button" className="btn btnPrimary btnSm" disabled={applying} onClick={() => void onApply()}>
          Apply
        </button>
      </div>
      <div className="help muted">Proposed — not applied yet.</div>
    </div>
  );
}

type AdvisorMessage = AiChatMessage & { proposedChange?: ModelConfigChange | null };
type AdvisorMessageRow = { id: string; role: string; content: string; created_at: string; proposed_change?: ModelConfigChange | null };
type SessionResponse = { session: { id: string; messages: AdvisorMessageRow[] } | null };

export function NodeAdvisorPanel({
  operationId,
  nodeId,
  applyModelConfigChange,
  resolveModelName,
}: {
  operationId: string;
  nodeId: string;
  applyModelConfigChange?: (nodeId: string, change: ModelConfigChange) => Promise<ApplyModelConfigResult>;
  resolveModelName?: (id: string) => string | null;
}): JSX.Element {
  const base = `/api/tenant/operations/${encodeURIComponent(operationId)}/nodes/${encodeURIComponent(nodeId)}/advisor/sessions`;
  const [draft, setDraft] = useState("");
  const [chatBusy, setChatBusy] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [sendErrorCode, setSendErrorCode] = useState<string | null>(null);
  const [runId, setRunId] = useState<string | null>(null);
  const chatPanelRef = useRef<HTMLDivElement | null>(null);
  // Latest runId for the cancel callback (which the hook holds by reference).
  const runIdRef = useRef<string | null>(null);
  runIdRef.current = runId;
  // The thread on screen, for Reset chat (fed from the hook's sessionId below).
  const sessionIdRef = useRef<string | null>(null);

  const {
    sessionId,
    setSessionId,
    messages,
    setMessages,
    replyPending,
    refresh,
    error: chatThreadError,
    resetChat,
    cancelChat,
  } = useAiChatThread<AdvisorMessage>({
    threadKey: `${operationId}/${nodeId}/model-advisor`,
    loadLatest: useCallback(async () => {
      const data = await apiFetch<SessionResponse>(`${base}/latest`);
      const messages: AdvisorMessage[] = (data.session?.messages ?? []).map((m) => ({
        id: m.id,
        role: m.role as AdvisorMessage["role"],
        content: m.content,
        created_at: m.created_at,
        proposedChange: m.proposed_change ?? null,
      }));
      return { sessionId: data.session?.id ?? null, messages };
    }, [base]),
    // Reset chat: the POST is get-or-create, so the thread on screen is deleted first.
    createNewSession: useCallback(async () => {
      return startNewAdvisorThread(base, sessionIdRef.current);
    }, [base]),
    cancelChat: useCallback(async () => {
      const rid = runIdRef.current;
      if (rid) {
        try {
          await apiFetch(`/api/tenant/agent-runs/${encodeURIComponent(rid)}`, { method: "DELETE" });
        } catch {
          /* best-effort; the agent persists a terminal "Stopped." either way */
        }
      }
    }, []),
  });
  sessionIdRef.current = sessionId;

  // SSE: a fast flush when the run ends on THIS mount. The hook's poll-while-
  // replyPending is the durable fallback (covers click-away / remount).
  const { status } = useAgentRunStream(runId);
  useEffect(() => {
    if (runId && (status === "ended" || status === "error")) {
      void refresh().finally(() => setRunId(null));
    }
  }, [runId, status, refresh]);
  // Drop the local run (close the SSE) once a reply lands via either path.
  useEffect(() => {
    const last = messages[messages.length - 1];
    if (runId && last && last.role === "assistant") setRunId(null);
  }, [messages, runId]);

  // Pin the scroll region to the latest message.
  useEffect(() => {
    const el = chatPanelRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, replyPending]);

  const inFlight = chatBusy || replyPending;

  const send = useCallback(async () => {
    const text = draft.trim();
    if (!text || inFlight) return;
    setChatBusy(true);
    try {
      let sid = sessionId;
      if (!sid) {
        const created = await apiFetch<{ session: { id: string } }>(base, { method: "POST" });
        sid = created.session.id;
        setSessionId(sid);
      }
      setMessages((prev) => [
        ...prev,
        { id: `local-${Date.now()}`, role: "user", content: text, created_at: new Date().toISOString() },
      ]);
      setDraft("");
      setSendError(null);
      setSendErrorCode(null);
      const res = await apiFetch<{ run_id: string }>(`${base}/${encodeURIComponent(sid)}/messages`, {
        method: "POST",
        body: { message: text },
      });
      setRunId(res.run_id);
    } catch (e) {
      setSendError(e instanceof Error ? e.message : String(e));
      setSendErrorCode(aiRouteErrorCode(e));
      void refresh();
    } finally {
      // Hand pending off to the hook (replyPending tracks the trailing user turn).
      setChatBusy(false);
    }
  }, [draft, inFlight, sessionId, setSessionId, setMessages, base, refresh]);

  return (
    <div className="askChatPanel askChatPanelFill">
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <button
          type="button"
          className="btn btnSm"
          onClick={() => void resetChat()}
          disabled={messages.length === 0 || inFlight}
          title="Start a new conversation for this node."
        >
          Reset chat
        </button>
      </div>
      <div className="askChatMessages" ref={chatPanelRef}>
        <ChatMessageList
          messages={messages}
          assistantLabel="Advisor"
          emptyState={
            <div className="help">
              Ask what this node does, how to configure it, what columns to add, where the model is weak, or how to set up scoring or monitoring. To change the configuration, just say so (e.g. "add queue depth and drop the part number").
            </div>
          }
          renderAssistantExtras={(m) =>
            m.proposedChange && applyModelConfigChange ? (
              <ProposedChangeCard
                key={`${m.id}-change`}
                change={m.proposedChange}
                nodeId={nodeId}
                apply={applyModelConfigChange}
                resolveModelName={resolveModelName}
              />
            ) : null
          }
          trailingNode={<ChatThinkingIndicator visible={inFlight} />}
        />
      </div>
      <ChatErrorBanner message={sendError ?? chatThreadError} code={sendError ? sendErrorCode : null} />
      <ChatComposer
        draft={draft}
        setDraft={setDraft}
        onSend={() => void send()}
        onCancel={() => void cancelChat()}
        inFlight={inFlight}
        placeholder="Ask the advisor…"
      />
    </div>
  );
}
